import { doDateRangesOverlap, parseDateTime } from 'async-timing';
import { DateRangeType } from 'application-types';
import { REMAINING_SPOTS_THRESHOLD } from './constants';
import { ReservableAvailabilityReason } from './types';

export enum GuestCapacityNoteVariantEnum {
  /** The only remaining spot is taken by the booker. */
  NoGuestSpots = 'NoGuestSpots',
  /** Exactly one guest fits. */
  SingleGuest = 'SingleGuest',
  /** Two or more guests fit. */
  MultipleGuests = 'MultipleGuests',
  /** The member has reached the maximum bookable consecutive slots for this time. */
  UserCapReached = 'UserCapReached',
}

export type GuestCapacityNote = {
  variant: GuestCapacityNoteVariantEnum;
  /** `min(remainingSpots - 1, maxGuestsPerBooking)` — guests that still fit. Zero for UserCapReached. */
  invitableGuests: number;
};

/**
 * The narrow slice of a reservable availability range this helper needs. Kept
 * structural so callers can pass their availability ranges straight through.
 */
type SlotAvailability = {
  interval: DateRangeType;
  remainingSpots?: number | null;
  reason?: ReservableAvailabilityReason | null;
};

type GetGuestCapacityNoteForSlotParams = {
  /** The slot the member currently has selected, if any. */
  selectedRange?: DateRangeType | null;
  /** Server-computed availability covering the day being browsed. */
  availabilityRanges?: SlotAvailability[];
  /** Max guests a single booking may invite, from the GuestInvite feature. */
  maxGuestsPerBooking?: number | null;
  /**
   * Total capacity for a slot, used as the remaining spots when the
   * selection has no matching entry in `availabilityRanges` — the server
   * only returns entries for slots with at least one reservation, so a
   * fully open slot is otherwise indistinguishable from an unknown one.
   */
  maxQuantityPerSlot?: number | null;
  /**
   * The slot the interaction being edited already occupies, captured when
   * the edit form loaded. That booking is already counted server-side, so
   * re-selecting it is not new consumption of the per-user cap.
   */
  originalSlot?: DateRangeType | null;
};

function isPositiveInteger(value?: number | null): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

// Exact equality, not overlap: custom time slots can have arbitrary,
// overlapping durations, so a merely-overlapping slot is still a different
// one the member is newly consuming capacity for.
function isSameSlot(a: DateRangeType, b?: DateRangeType | null): boolean {
  if (!b) {
    return false;
  }

  const aStart = parseDateTime(a.startDate);
  const aEnd = parseDateTime(a.endDate);
  const bStart = parseDateTime(b.startDate);
  const bEnd = parseDateTime(b.endDate);

  return Boolean(
    aStart &&
      aEnd &&
      bStart &&
      bEnd &&
      aStart.toMillis() === bStart.toMillis() &&
      aEnd.toMillis() === bEnd.toMillis()
  );
}

// The member has reached their per-slot booking limit, and this isn't the
// slot the booking being edited already occupies (which wouldn't be new
// capacity consumption).
function shouldWarnAboutCapacity(
  availability: SlotAvailability | undefined,
  selectedRange: DateRangeType,
  originalSlot?: DateRangeType | null
): boolean {
  return (
    availability?.reason === ReservableAvailabilityReason.UserCapReached &&
    !isSameSlot(selectedRange, originalSlot)
  );
}

// No matching entry means zero reservations were made against this slot,
// not that the slot is unavailable — fall back to its full capacity.
function resolveRemainingSpots(
  availability: SlotAvailability | undefined,
  maxQuantityPerSlot?: number | null
): number | null | undefined {
  return availability ? availability.remainingSpots : maxQuantityPerSlot;
}

/**
 * Resolves the guest capacity note for the currently selected slot, or `null`
 * when there is nothing useful to say. Callers own turning `variant` into
 * display copy (e.g. an i18n translation key) — this stays UI-agnostic so
 * both web and mobile can share it.
 *
 * Returns `null` when:
 * - no slot is selected
 * - remaining spots are unknown or non-positive (the slot already reads "Full")
 * - remaining spots exceed {@link REMAINING_SPOTS_THRESHOLD}
 * - guests are disabled for the booking (`maxGuestsPerBooking` <= 0)
 *
 * Returns `UserCapReached` variant when:
 * - the selected slot has reason `UserCapReached`, meaning the member has
 *   already booked this time slot the maximum allowed number of times
 * - unless `selectedRange` is the same slot as `originalSlot`, in which case
 *   the cap is not re-evaluated for a booking that already holds that slot.
 */
export function getGuestCapacityNoteForSlot({
  selectedRange,
  availabilityRanges,
  maxGuestsPerBooking,
  maxQuantityPerSlot,
  originalSlot,
}: GetGuestCapacityNoteForSlotParams): GuestCapacityNote | null {
  if (!selectedRange?.startDate || !selectedRange?.endDate) {
    return null;
  }

  const selectedAvailability = availabilityRanges?.find(range =>
    doDateRangesOverlap(selectedRange, range.interval)
  );

  if (shouldWarnAboutCapacity(selectedAvailability, selectedRange, originalSlot)) {
    return {
      variant: GuestCapacityNoteVariantEnum.UserCapReached,
      invitableGuests: 0,
    };
  }

  const remainingSpots = resolveRemainingSpots(
    selectedAvailability,
    maxQuantityPerSlot
  );

  if (!isPositiveInteger(remainingSpots) || !isPositiveInteger(maxGuestsPerBooking)) {
    return null;
  }

  if (remainingSpots > REMAINING_SPOTS_THRESHOLD) {
    return null;
  }

  if (remainingSpots === 1) {
    return {
      variant: GuestCapacityNoteVariantEnum.NoGuestSpots,
      invitableGuests: 0,
    };
  }

  const invitableGuests = Math.min(remainingSpots - 1, maxGuestsPerBooking);

  return {
    variant:
      invitableGuests === 1
        ? GuestCapacityNoteVariantEnum.SingleGuest
        : GuestCapacityNoteVariantEnum.MultipleGuests,
    invitableGuests,
  };
}
